/**
 * Strimy Ads, browser side: types for the /api/ads/* responses, a fetch
 * wrapper that never throws, and the small pure rules the advertiser and admin
 * pages share (formatting, which buttons a status gets, upload checks).
 *
 * Client-safe on purpose: adsCore.ts imports Node's `crypto`, so the few
 * constants needed here are mirrored and adsClient.test.ts keeps them equal.
 */

// ── response shapes ───────────────────────────────────────────────────────
export type AdsTerms = {
  cpmCents: number;
  streamerSharePercent: number;
  maxAdSeconds: number;
  minBudgetCents: number;
  holdDays: number;
  minFollowers: number;
  minStreams30d: number;
};

export type AdsForecast = {
  cpmCents: number;
  views: number;
  dailyViews: number;
  days: number | null;
  label: string;
  basis: 'ad_channels' | 'whole_platform';
};

export type Advertiser = {
  id: string;
  business_name: string;
  website_url: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  logo_url: string | null;
  status: 'active' | 'suspended';
  owner_id?: string;
};

export type CampaignStatus =
  | 'draft'
  | 'pending_review'
  | 'approved'
  | 'active'
  | 'paused'
  | 'completed'
  | 'rejected';

export type CreativeStatus = 'uploading' | 'processing' | 'ready' | 'errored';
export type Orientation = 'portrait' | 'landscape';

export type Creative = {
  id: string;
  orientation: Orientation;
  status: CreativeStatus;
  duration_sec: number | null;
  error: string | null;
  playback_url: string | null;
  poster_url: string | null;
};

export type Campaign = {
  id: string;
  name: string;
  headline: string;
  cta_label: string;
  cta_url: string;
  status: CampaignStatus;
  review_note: string | null;
  budget_cents: number;
  funded_cents: number;
  spent_cents: number;
  remaining_cents: number;
  daily_cap_cents: number | null;
  cpm_cents: number;
  category_ids: string[] | null;
  countries: string[] | null;
  exclude_mature: boolean;
  start_at: string;
  end_at: string | null;
  views: number;
  created_at: string;
  submitted_at: string | null;
  creatives: Creative[];
};

export type CampaignDay = {
  day: string;
  served: number;
  views: number;
  clicks: number;
  spent_cents: number;
};

export type CampaignDetail = Campaign & { served: number; clicks: number; daily: CampaignDay[] };

export type AdminCampaign = Campaign & {
  advertiser: (Advertiser & { owner_id: string }) | null;
};

export type AdminOverview = {
  activeCampaigns: number;
  pendingReview: number;
  fundedCents: number;
  spentCents: number;
  served30d: number;
  views30d: number;
  topCreatorDays: Array<{
    streamer_id: string;
    day: string;
    views: number;
    streamer_millicents: number;
    status: string;
  }>;
};

// ── fetch ─────────────────────────────────────────────────────────────────
export const ADS_UNAVAILABLE = 'Ads aren’t available yet. Please try again later.';

export type AdsResult<T> =
  | { ok: true; status: number; data: T }
  | { ok: false; status: number; error: string; code?: string };

/**
 * The words to show for a failed /api/ads call. Server messages are written
 * for people, so they're shown as given; a missing route, a crash or a
 * non-JSON reply means the ads system isn't switched on here yet.
 */
export function adsErrorMessage(status: number, body: unknown): string {
  const err =
    body && typeof body === 'object' && typeof (body as { error?: unknown }).error === 'string'
      ? ((body as { error: string }).error || '').trim()
      : '';
  if (status === 401) return err || 'Sign in to continue.';
  if (status === 0) return 'Network problem. Check your connection and try again.';
  return err || ADS_UNAVAILABLE;
}

/** Same-origin call with the cookie session. Never throws. */
export async function adsFetch<T>(
  path: string,
  init: { method?: string; body?: unknown; signal?: AbortSignal } = {}
): Promise<AdsResult<T>> {
  let res: Response;
  try {
    res = await fetch(path, {
      method: init.method || 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: init.body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
      body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
      signal: init.signal,
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    return { ok: false, status: 0, error: adsErrorMessage(0, null) };
  }
  const body: unknown = await res.json().catch(() => null);
  if (!res.ok || body == null) {
    const code =
      body && typeof body === 'object' && typeof (body as { code?: unknown }).code === 'string'
        ? (body as { code: string }).code
        : undefined;
    // An HTML 404 (route not deployed) has no JSON body: treat as unavailable.
    return {
      ok: false,
      status: res.status,
      error: body == null ? ADS_UNAVAILABLE : adsErrorMessage(res.status, body),
      code,
    };
  }
  return { ok: true, status: res.status, data: body as T };
}

// ── terms and money ───────────────────────────────────────────────────────
/** Mirrors DEFAULT_ADS_SETTINGS (adsCore) until GET /api/ads/settings answers. */
export const DEFAULT_TERMS: AdsTerms = {
  cpmCents: 200,
  streamerSharePercent: 70,
  maxAdSeconds: 15,
  minBudgetCents: 500,
  holdDays: 14,
  minFollowers: 25,
  minStreams30d: 3,
};

/** Mirrors adsCore CTA_LABELS (adsClient.test.ts checks they match). */
export const CTA_LABELS = [
  'Learn more',
  'Shop now',
  'Sign up',
  'Download',
  'Visit',
  'Call now',
  'Book now',
] as const;

export const HEADLINE_MAX = 60;
export const NAME_MAX = 80;

/** Same rule as adsCore viewsForBudget. */
export function viewsForBudget(budgetCents: number, cpmCents: number): number {
  return cpmCents > 0 ? Math.floor((budgetCents * 1000) / cpmCents) : 0;
}

/** $10.00, $1,250.50 */
export function formatUsd(cents: number | null | undefined): string {
  const n = Number(cents) || 0;
  return `$${(n / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** $10, $12.50: whole dollars without the cents. */
export function formatUsdShort(cents: number | null | undefined): string {
  const n = Number(cents) || 0;
  return n % 100 === 0 ? `$${(n / 100).toLocaleString('en-US')}` : formatUsd(n);
}

export function formatCount(n: number | null | undefined): string {
  return (Number(n) || 0).toLocaleString('en-US');
}

/** Click-through rate as "2.4%" ("—" with nothing served). */
export function formatCtr(clicks: number, served: number): string {
  if (!served) return '—';
  const pct = (clicks / served) * 100;
  return `${pct >= 10 || pct === 0 ? pct.toFixed(0) : pct.toFixed(1)}%`;
}

/** "10", "10.5", "$1,000" → cents; null if it isn't an amount. */
export function parseDollarsToCents(input: string): number | null {
  const s = String(input ?? '')
    .replace(/[$,\s]/g, '')
    .trim();
  if (!/^\d+(\.\d{0,2})?$/.test(s)) return null;
  return Math.round(Number(s) * 100);
}

export function centsToDollarsInput(cents: number | null | undefined): string {
  if (cents == null) return '';
  const n = Number(cents);
  return n % 100 === 0 ? String(n / 100) : (n / 100).toFixed(2);
}

/**
 * The forecast line under a budget: the server's label as given, with
 * "at today’s audience" added when the label doesn't already say it.
 */
export function forecastSentence(f: Pick<AdsForecast, 'days' | 'label'>): string {
  if (f.days == null || /audience/i.test(f.label)) return f.label;
  return `${f.label} at today’s audience`;
}

// ── statuses ──────────────────────────────────────────────────────────────
export type StatusTone = 'neutral' | 'info' | 'warning' | 'good' | 'critical';

export const STATUS_META: Record<
  CampaignStatus,
  { label: string; tone: StatusTone; help: string }
> = {
  draft: {
    label: 'Draft',
    tone: 'neutral',
    help: 'Finish the details and upload your video, then submit it for review.',
  },
  pending_review: {
    label: 'In review',
    tone: 'info',
    help: 'The Strimy team is checking your ad. You’ll be able to pay once it’s approved.',
  },
  approved: {
    label: 'Approved · awaiting payment',
    tone: 'warning',
    help: 'Your ad is approved. Pay the budget and it starts running.',
  },
  active: { label: 'Running', tone: 'good', help: 'Your ad is showing on live streams.' },
  paused: {
    label: 'Paused',
    tone: 'neutral',
    help: 'Your ad isn’t showing. Resume it whenever you like.',
  },
  completed: {
    label: 'Completed',
    tone: 'neutral',
    help: 'The budget has been spent. Top up to keep it running.',
  },
  rejected: {
    label: 'Changes needed',
    tone: 'critical',
    help: 'Your ad wasn’t approved. Read the note, make changes and resubmit.',
  },
};

export function statusMeta(status: string) {
  return (
    STATUS_META[status as CampaignStatus] || { label: status, tone: 'neutral' as const, help: '' }
  );
}

/** campaignPaymentAmount (ads.ts) refuses smaller top-ups. */
export const MIN_TOP_UP_CENTS = 500;

/** Unpaid part of the budget. */
export function amountDueCents(c: Pick<Campaign, 'budget_cents' | 'funded_cents'>): number {
  return Math.max(0, Number(c.budget_cents) - Number(c.funded_cents));
}

export type CampaignActions = {
  /** Video and everything viewers see can change (draft, rejected). */
  canEditAd: boolean;
  canUpload: boolean;
  canSubmit: boolean;
  /** Pay the unpaid budget. */
  canPay: boolean;
  /** Add money beyond the budget. */
  canTopUp: boolean;
  canPause: boolean;
  canResume: boolean;
};

/** Which controls a campaign gets, mirroring the server's rules (ads.ts). */
export function campaignActions(
  c: Pick<Campaign, 'status' | 'budget_cents' | 'funded_cents'> & {
    creatives?: Pick<Creative, 'orientation' | 'status'>[];
  }
): CampaignActions {
  const s = c.status;
  const editable = s === 'draft' || s === 'rejected';
  const portraitReady = (c.creatives || []).some(
    (x) => x.orientation === 'portrait' && x.status === 'ready'
  );
  const due = amountDueCents(c);
  return {
    canEditAd: editable,
    canUpload: editable,
    canSubmit: editable && portraitReady,
    canPay: s === 'approved' ? due > 0 : (s === 'active' || s === 'paused') && due > 0,
    canTopUp: s === 'active' || s === 'paused' || s === 'completed',
    canPause: s === 'active',
    canResume: s === 'paused',
  };
}

export type CampaignField =
  | 'name'
  | 'headline'
  | 'cta_label'
  | 'cta_url'
  | 'budget_cents'
  | 'daily_cap_cents'
  | 'start_at'
  | 'end_at'
  | 'category_ids'
  | 'countries'
  | 'exclude_mature';

export const ALL_CAMPAIGN_FIELDS: CampaignField[] = [
  'name',
  'headline',
  'cta_label',
  'cta_url',
  'budget_cents',
  'daily_cap_cents',
  'start_at',
  'end_at',
  'category_ids',
  'countries',
  'exclude_mature',
];

/**
 * What updateCampaign accepts in each status: everything before approval (or
 * after a rejection); afterwards only money and dates, never the ad itself.
 * Completed campaigns are extended with a top-up instead.
 */
export function editableFields(status: string): CampaignField[] {
  if (status === 'draft' || status === 'rejected') return ALL_CAMPAIGN_FIELDS;
  if (
    status === 'pending_review' ||
    status === 'approved' ||
    status === 'active' ||
    status === 'paused'
  )
    return ['budget_cents', 'daily_cap_cents', 'start_at', 'end_at'];
  return [];
}

/** Only the fields that changed, so a PATCH never resets something by accident. */
export function changedFields<T extends Partial<Record<CampaignField, unknown>>>(
  before: T,
  after: T,
  allowed: CampaignField[]
): Partial<T> {
  const out: Partial<T> = {};
  for (const k of allowed) {
    if (!(k in after)) continue;
    const a = JSON.stringify(before[k] ?? null);
    const b = JSON.stringify(after[k] ?? null);
    if (a !== b) (out as Record<string, unknown>)[k] = after[k];
  }
  return out;
}

// ── targeting ─────────────────────────────────────────────────────────────
/** "ug, KE  tz" → ['UG','KE','TZ']; anything that isn't two letters is returned as invalid. */
export function parseCountries(input: string): { codes: string[]; invalid: string[] } {
  const parts = String(input || '')
    .split(/[\s,;]+/)
    .map((x) => x.trim())
    .filter(Boolean);
  const codes: string[] = [];
  const invalid: string[] = [];
  for (const p of parts) {
    if (/^[A-Za-z]{2}$/.test(p)) {
      const u = p.toUpperCase();
      if (!codes.includes(u)) codes.push(u);
    } else invalid.push(p);
  }
  return { codes, invalid };
}

export const COMMON_COUNTRIES: Array<[string, string]> = [
  ['UG', 'Uganda'],
  ['KE', 'Kenya'],
  ['TZ', 'Tanzania'],
  ['RW', 'Rwanda'],
  ['NG', 'Nigeria'],
  ['GH', 'Ghana'],
  ['ZA', 'South Africa'],
  ['US', 'United States'],
  ['GB', 'United Kingdom'],
];

// ── dates ─────────────────────────────────────────────────────────────────
const pad = (n: number) => String(n).padStart(2, '0');

/** ISO → the value a <input type="datetime-local"> wants, in the viewer's clock. */
export function isoToLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** datetime-local value (viewer's clock) → ISO, or null when empty/invalid. */
export function localInputToIso(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function formatDay(day: string): string {
  const d = new Date(`${day}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return day;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// ── video uploads ─────────────────────────────────────────────────────────
/** Mux allows a little slack over the limit (ads.ts accepts max + 0.5 s). */
export const DURATION_SLACK_SEC = 0.5;

/**
 * Client-side check before uploading (the server checks again once Mux has
 * the file). Unreadable metadata isn't blocked here: the server decides.
 */
export function checkAdDuration(durationSec: number | null, maxSec: number): string | null {
  if (durationSec == null || !Number.isFinite(durationSec) || durationSec <= 0) return null;
  if (durationSec > maxSec + DURATION_SLACK_SEC) {
    return `This video is ${Math.round(durationSec)} seconds. Ads can be up to ${maxSec} seconds — trim it and try again.`;
  }
  return null;
}

export function orientationOf(width: number, height: number): Orientation | null {
  if (!width || !height) return null;
  return height >= width ? 'portrait' : 'landscape';
}

/** A gentle warning when a landscape file goes in the portrait slot (or the other way). */
export function orientationWarning(
  slot: Orientation,
  width: number,
  height: number
): string | null {
  const o = orientationOf(width, height);
  if (!o || o === slot) return null;
  return slot === 'portrait'
    ? 'This video is landscape. The portrait slot works best with a vertical (9:16) video.'
    : 'This video is portrait. The landscape slot works best with a horizontal (16:9) video.';
}

export function isVideoFile(file: Pick<File, 'type' | 'name'>): boolean {
  return /^video\//.test(file.type) || /\.(mp4|mov|m4v|webm|mkv)$/i.test(file.name);
}

/** Should the page keep polling for this video? */
export function creativePending(c: Pick<Creative, 'status'> | null | undefined): boolean {
  return !!c && (c.status === 'uploading' || c.status === 'processing');
}

/** Read a local video's duration and size without uploading it (browser only). */
export function readVideoMetadata(
  file: File
): Promise<{ duration: number | null; width: number; height: number }> {
  return new Promise((resolve) => {
    if (typeof document === 'undefined') {
      resolve({ duration: null, width: 0, height: 0 });
      return;
    }
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    let done = false;
    const finish = (r: { duration: number | null; width: number; height: number }) => {
      if (done) return;
      done = true;
      window.clearTimeout(timer);
      URL.revokeObjectURL(url);
      video.removeAttribute('src');
      resolve(r);
    };
    const timer = window.setTimeout(() => finish({ duration: null, width: 0, height: 0 }), 10_000);
    video.preload = 'metadata';
    video.muted = true;
    video.onloadedmetadata = () =>
      finish({
        duration: Number.isFinite(video.duration) ? video.duration : null,
        width: video.videoWidth,
        height: video.videoHeight,
      });
    video.onerror = () => finish({ duration: null, width: 0, height: 0 });
    video.src = url;
  });
}

/** PUT a file to a Mux direct-upload URL with progress (0–1). */
export function uploadWithProgress(
  url: string,
  file: File,
  onProgress: (fraction: number) => void
): { promise: Promise<void>; abort: () => void } {
  const xhr = new XMLHttpRequest();
  const promise = new Promise<void>((resolve, reject) => {
    xhr.open('PUT', url);
    if (file.type) xhr.setRequestHeader('Content-Type', file.type);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total);
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`The upload failed (${xhr.status}). Please try again.`));
    xhr.onerror = () =>
      reject(new Error('The upload failed. Check your connection and try again.'));
    xhr.onabort = () => reject(new Error('Upload cancelled.'));
    xhr.send(file);
  });
  return { promise, abort: () => xhr.abort() };
}

// ── landing page slider ───────────────────────────────────────────────────
const BUDGET_STOPS_DOLLARS = [
  10, 15, 20, 25, 30, 40, 50, 75, 100, 150, 200, 250, 300, 400, 500, 750, 1000, 1500, 2000, 2500,
  3000, 4000, 5000,
];

/** Slider positions in cents, starting at the live minimum budget. */
export function budgetStops(minBudgetCents: number): number[] {
  const stops = BUDGET_STOPS_DOLLARS.map((d) => d * 100).filter((c) => c > minBudgetCents);
  return [minBudgetCents, ...stops];
}

/** Index of the stop nearest to a budget. */
export function nearestStop(stops: number[], cents: number): number {
  let best = 0;
  for (let i = 1; i < stops.length; i++) {
    if (Math.abs(stops[i] - cents) < Math.abs(stops[best] - cents)) best = i;
  }
  return best;
}

// ── form checks (the server re-checks everything) ────────────────────────
export type CampaignPayload = {
  name?: string;
  headline?: string;
  cta_label?: string;
  cta_url?: string;
  budget_cents?: number | null;
  daily_cap_cents?: number | null;
  start_at?: string | null;
  end_at?: string | null;
  category_ids?: string[] | null;
  countries?: string[] | null;
  exclude_mature?: boolean;
};

/** First problem with the fields present, in the server's words, or null. */
export function campaignFormError(
  p: CampaignPayload,
  o: { minBudgetCents: number; fundedCents?: number; nowMs: number }
): string | null {
  if ('name' in p) {
    const n = (p.name || '').trim();
    if (!n || n.length > NAME_MAX) return 'Give the campaign a name (up to 80 characters).';
  }
  if ('headline' in p) {
    const h = (p.headline || '').replace(/\s+/g, ' ').trim();
    if (!h || h.length > HEADLINE_MAX) return 'Add a headline of up to 60 characters.';
  }
  if ('cta_label' in p && !(CTA_LABELS as readonly string[]).includes(p.cta_label || ''))
    return 'Pick a button label.';
  if ('cta_url' in p) {
    try {
      if (new URL((p.cta_url || '').trim()).protocol !== 'https:') throw new Error();
    } catch {
      return 'The link must be a full https:// address.';
    }
  }
  if ('budget_cents' in p) {
    const b = p.budget_cents;
    if (b == null || !Number.isFinite(b) || b < o.minBudgetCents)
      return `The minimum budget is ${formatUsd(o.minBudgetCents)}.`;
    if (o.fundedCents && b < o.fundedCents)
      return `The budget can’t be lower than what you’ve already paid (${formatUsd(o.fundedCents)}).`;
  }
  if ('daily_cap_cents' in p && p.daily_cap_cents != null) {
    if (!Number.isFinite(p.daily_cap_cents) || p.daily_cap_cents < 100)
      return 'A daily limit must be at least $1.';
  }
  if ('end_at' in p && p.end_at) {
    const end = new Date(p.end_at).getTime();
    const start = p.start_at ? new Date(p.start_at).getTime() : o.nowMs;
    if (!Number.isFinite(end) || end <= Math.max(start, o.nowMs - 60_000) + 60 * 60 * 1000)
      return 'The end date must be at least an hour after the start.';
  }
  if ('countries' in p && p.countries && p.countries.length > 30) return 'Pick up to 30 countries.';
  if ('category_ids' in p && p.category_ids && p.category_ids.length > 20)
    return 'Pick up to 20 categories.';
  return null;
}

// ── business dashboard ────────────────────────────────────────────────────
export type AdvertiserOverview = {
  advertiser: Advertiser | null;
  campaigns: Campaign[];
  totals: {
    campaigns: number;
    running: number;
    in_review: number;
    drafts: number;
    to_pay: number;
    due_cents: number;
    funded_cents: number;
    spent_cents: number;
    remaining_cents: number;
    views: number;
  };
  period: {
    days: number;
    served: number;
    views: number;
    clicks: number;
    spent_cents: number;
    click_rate: number;
    views_7d: number;
    views_prev_7d: number;
    spent_7d: number;
  };
  daily: CampaignDay[];
};

export type AdPayment = {
  reference: string;
  method: 'card' | 'mobile_money';
  status: string;
  amount: number;
  currency: string;
  usd_cents: number;
  campaign_id: string | null;
  campaign_name: string | null;
  invoice_number: string | null;
  pay_link: string | null;
  failure_reason: string | null;
  created_at: string;
  paid_at: string | null;
};

export type SetupStep = { key: string; label: string; done: boolean; href: string };

/** The getting-started checklist: business → campaign → video → review → paid. */
export function setupSteps(advertiser: Advertiser | null, campaigns: Campaign[]): SetupStep[] {
  const first = campaigns[campaigns.length - 1];
  const withVideo = campaigns.find((c) => c.creatives?.some((x) => x.status === 'ready'));
  const submitted = campaigns.find((c) => c.status !== 'draft');
  const paid = campaigns.some((c) => Number(c.funded_cents) > 0);
  const open = (c?: Campaign) => (c ? `/campaigns/${c.id}` : '/campaigns/new');
  return [
    { key: 'business', label: 'Add your business details', done: !!advertiser, href: '/settings' },
    { key: 'campaign', label: 'Create a campaign', done: campaigns.length > 0, href: open() },
    {
      key: 'video',
      label: 'Upload your video ad',
      done: !!withVideo,
      href: open(withVideo || first),
    },
    {
      key: 'review',
      label: 'Submit it for review',
      done: !!submitted,
      href: open(submitted || withVideo || first),
    },
    {
      key: 'pay',
      label: 'Pay and go live',
      done: paid,
      href: open(campaigns.find((c) => c.status === 'approved') || submitted || first),
    },
  ];
}

export type CampaignFilter = 'all' | 'running' | 'review' | 'pay' | 'drafts' | 'ended';

export const CAMPAIGN_FILTERS: Array<{ key: CampaignFilter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'running', label: 'Running' },
  { key: 'review', label: 'In review' },
  { key: 'pay', label: 'To pay' },
  { key: 'drafts', label: 'Drafts' },
  { key: 'ended', label: 'Ended' },
];

export function matchesCampaignFilter(c: Campaign, f: CampaignFilter): boolean {
  switch (f) {
    case 'running':
      return c.status === 'active' || c.status === 'paused';
    case 'review':
      return c.status === 'pending_review';
    case 'pay':
      return c.status === 'approved' && amountDueCents(c) > 0;
    case 'drafts':
      return c.status === 'draft' || c.status === 'rejected';
    case 'ended':
      return c.status === 'completed';
    default:
      return true;
  }
}

/** Week-on-week change as a fraction, or null when last week had nothing. */
export function weekChange(current: number, previous: number): number | null {
  if (!previous) return null;
  return (current - previous) / previous;
}
